/**
 * Membership vocabulary, as the API defines it.
 *
 * The values are the server's — `src/const/membership.js`, `orderNatures` and
 * `ORDER_TYPES` in the API. Only the wording is ours. Keeping the labels here rather than
 * inline in each screen means the tier form, the member profile and the order modal can
 * never describe the same state three different ways.
 */

export const MEMBERSHIP_STATUSES = ["pending", "active", "suspended", "expired", "revoked"];

export const STATUS_LABELS = {
    pending: "Pending",
    active: "Active",
    suspended: "Suspended",
    expired: "Expired",
    revoked: "Revoked",
};

/**
 * Which statuses an admin may set by hand.
 *
 * `expired` is absent on purpose: it is reached by the calendar, not by a decision. The
 * nightly job sets it, and the read path already refuses a lapsed card the moment it
 * lapses. To end a card early, revoke it — that records a reason, which "expired" cannot.
 */
export const SETTABLE_STATUSES = ["pending", "active", "suspended", "revoked"];

/*
 * VERIFICATION_STATUSES / VERIFICATION_LABELS used to live here.
 *
 * The column survives on Memberships, but nothing in the pricing path ever read it, so a
 * "Verified" badge asserted a check nobody had performed — the same fault the photo gate
 * had. The API no longer accepts it and no screen shows it. `status` is the one gate, and
 * an admin opens it deliberately.
 */

/** Platform the order came from. Empty list = every platform. */
export const ORDER_NATURES = [
    { value: "normal", label: "Pickup App" },
    { value: "loyalty", label: "POS (counter)" },
    { value: "waiter", label: "Waiter" },
    { value: "kiosk", label: "Kiosk" },
    { value: "thirdParty", label: "Third-party (aggregators)" },
];

/** How the order is fulfilled. Empty list = every type. */
export const ORDER_TYPES = [
    { value: "Pickup", label: "Pickup" },
    { value: "Curbside", label: "Curbside" },
    { value: "Drivethrough", label: "Drive-thru" },
    { value: "Dinein", label: "Dine-in" },
    { value: "Delivery", label: "Delivery" },
];

/**
 * Why a discount did not apply, keyed by the server's reason code.
 *
 * Shown on the order detail modal and in the scan log. These are the codes the API
 * returns from `evaluateMembershipForCart`, so a new one appearing here unlabelled means
 * the API grew a reason and this list needs the wording — not that anything is broken.
 */
export const BLOCK_REASON_LABELS = {
    program_disabled: "Membership is switched off for this merchant",
    no_membership: "Customer has no membership",
    membership_inactive: "Card is not active",
    membership_expired: "Card has expired",
    type_inactive: "Tier is deactivated",
    type_window: "Outside the tier's effective dates",
    branch_ineligible: "Tier does not cover this branch",
    channel_ineligible: "Tier does not cover this order channel",
    no_eligible_items: "Nothing in the basket qualified",
    max_order_value: "Basket is above the tier's maximum order value",
    usage_limit: "A usage cap was already reached",
    not_combinable: "Tier cannot be combined with a coupon",
    qr_disabled: "The card's QR code is disabled",
    invalid_qr: "QR code failed verification",
};

/** Which cap tripped, when the reason is `usage_limit`. */
export const LIMIT_LABELS = {
    daily_orders: "Daily order limit",
    monthly_orders: "Monthly order limit",
    daily_value: "Daily discount budget",
};

/**
 * Why an order earned no loyalty, keyed by `Order.loyaltyEarnSkipReason`.
 *
 * This column exists because "why did I get no points?" was previously answerable only
 * from server logs. `membership_no_points` is the one that ties the two programmes
 * together: the member took a discount and their tier does not also grant points.
 */
export const LOYALTY_SKIP_LABELS = {
    membership_no_points: "Membership order — this tier does not also earn points",
    // Distinct from the above on purpose: that one reverses itself on the member's next
    // undiscounted order, this one holds on every order they place. Only one of them is
    // answered with "your cap was spent, try tomorrow".
    membership_loyalty_excluded: "This tier's members are outside the loyalty programme",
    below_minimum: "Order was below the programme's minimum",
    no_eligible_items: "No eligible items after discounts",
    unusable_amount: "Earning base could not be determined",
};

/**
 * When members of a tier earn loyalty. Four values, and they are a LADDER from most
 * restrictive to least — presented as one radio group rather than as separate switches,
 * because they answer a single question and the combinations of separate booleans would
 * include several that contradict each other.
 *
 * The three situations behind it: a discount applied / the card works but gave nothing
 * (cap spent, item excluded, branch not covered) / the card does not work at all.
 */
export const LOYALTY_POLICIES = [
    {
        value: "never",
        label: "Never",
        summary: "Membership only — the loyalty card is hidden in the app",
        detail:
            "Members of this tier never earn points or stamps, on any order, whatever their card's status. " +
            "Note the consequence: a revoked member gets neither the discount nor points, and stays that way — " +
            "memberships are never deleted. The way back is to move them to another tier. " +
            "If that is not what you want, pick the next option instead.",
    },
    {
        value: "when_membership_inactive",
        label: "Only once their card stops working",
        summary: "Membership only while the card is active; ordinary customer afterwards",
        detail:
            "No points while the card works — including on orders where the discount did not apply. " +
            "The moment it is suspended, revoked or expired, the member goes back to earning like any customer.",
    },
    {
        value: "when_no_discount",
        label: "Only when the discount does not apply",
        summary: "The default — no points on top of a discount",
        detail:
            "The member earns normally on any order that did not receive the membership discount: the cap was " +
            "spent, nothing in the basket qualified, the branch was not covered. Only discounted orders earn nothing.",
    },
    {
        value: "always",
        label: "Always, including on discounted orders",
        summary: "Both benefits on every order",
        detail:
            "Points are earned on the amount left after the discount, not on the original total.",
    },
];

export const LOYALTY_POLICY_LABELS = Object.fromEntries(
    LOYALTY_POLICIES.map((p) => [p.value, p.label])
);

/** What the tier list shows in its narrow "Loyalty" column. */
export const LOYALTY_POLICY_BADGES = {
    never: { text: "Never", tone: "muted" },
    when_membership_inactive: { text: "Only when inactive", tone: "muted" },
    when_no_discount: { text: "Not on discounts", tone: "muted" },
    always: { text: "Always", tone: "good" },
};

export const AUDIT_ACTION_LABELS = {
    assigned: "Membership assigned",
    type_changed: "Tier changed",
    status_changed: "Status changed",
    overrides_changed: "Per-member limits changed",
    qr_rotated: "QR code changed",
    note_added: "Internal note added",
};

/**
 * What a configuration-history row is about. The API validates these and answers a typo
 * with a 400, so they are worth naming once rather than spelling out at each call site.
 */
export const CONFIG_ENTITIES = {
    membershipType: "membership_type",
    membershipGroup: "membership_group",
};

/**
 * Configuration history — changes to a TIER or a COMPANY, not to a member's card.
 *
 * Separate from AUDIT_ACTION_LABELS above because the two come from different tables and
 * answer different questions: that one is "what happened to this person", this one is
 * "who changed the terms everybody on this tier receives".
 */
export const CONFIG_ACTION_LABELS = {
    created: "Created",
    updated: "Settings changed",
    activated: "Activated",
    deactivated: "Deactivated",
    eligibility_changed: "Eligibility changed",
    handler_changed: "Account handler changed",
};

export const LEDGER_EVENT_LABELS = {
    apply: "Discount applied",
    revoke: "Reversed (return)",
    adjust: "Manual adjustment",
};

/**
 * Human labels for every field an audit diff can name — member overrides, tier settings
 * and company budgets alike.
 *
 * One map rather than three: the diff renderer is shared by all three logs, and a field
 * missing from here falls back to its raw column name, which is legible but ugly. That
 * fallback is deliberate — a new column appearing here unlabelled means this list needs
 * the wording, not that anything is broken.
 */
export const FIELD_LABELS = {
    // Member overrides and card fields
    maxOrderValue: "Max order value",
    maxDailyOrders: "Max orders per day",
    maxMonthlyOrders: "Max orders per month",
    maxDailyDiscountValue: "Daily discount budget",
    employeeId: "Employee ID",
    leaderId: "Leader",
    memberPhoto: "Photo",
    verificationStatus: "Verification",
    status: "Status",
    membershipTypeId: "Tier",
    membershipRef: "Card number",
    groupId: "Company",
    expiryDate: "Expiry date",
    issuedDate: "Issued date",
    qrStatus: "QR status",
    customerMessage: "Message shown to customer",
    internalNote: "Internal note",
    discountType: "Discount type",
    discountValue: "Discount value",
    memberLoyaltyPolicy: "Loyalty for members",

    // Tier settings
    name: "Name",
    arabicName: "Arabic name",
    code: "Code",
    isActive: "Active",
    cardColour: "Card colour",
    displayOrder: "Display order",
    effectiveDate: "Effective from",
    defaultValidityMonths: "Default validity (months)",
    applyToModifiers: "Applies to modifiers",
    combinableWithCoupons: "Combinable with coupons",
    applyToAllCategories: "All categories",
    requiresPhoto: "New members start as pending",
    allowedOrderNatures: "Order channels",
    allowedOrderTypes: "Fulfilment types",

    // Company budgets
    maxMonthlyDiscountValue: "Monthly discount budget",
    handlerMembershipId: "Account handler",

    // Not a column: the deactivation audit records how many active members the tier had
    // at the moment it was retired, because that is the size of the consequence.
    affectedMembers: "Members affected",
};

/** "15%" or "SAR 20.00" — the two discount shapes read very differently at a glance. */
export const formatDiscount = (discountType, discountValue) =>
    discountType === "amount"
        ? `SAR ${Number(discountValue ?? 0).toFixed(2)}`
        : `${Number(discountValue ?? 0)}%`;

/** A cap of null means NO limit, and must never render as "0". */
export const formatCap = (value, suffix = "") =>
    value === null || value === undefined || value === "" ? "No limit" : `${value}${suffix}`;
